import type { RideSchema, SimulationActivity } from '../../../../../types/simulation';

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return undefined;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function formatCoords(lat: number | null, lng: number | null): string | undefined {
  if (lat == null || lng == null) return undefined;
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

function extractActivityTrips(activity: SimulationActivity | null | undefined): Record<string, unknown>[] {
  if (!activity) return [];
  const raw = activity as Record<string, unknown>;
  const trips = raw.trips;
  if (!Array.isArray(trips)) return [];
  return trips.filter((t): t is Record<string, unknown> => Boolean(t) && typeof t === 'object');
}

function extractNestedTripRideAddresses(
  activity: SimulationActivity | null | undefined,
  leg: 'pickup' | 'dropoff'
): string | undefined {
  const trips = extractActivityTrips(activity);
  for (const trip of trips) {
    const ride = trip.ride;
    if (!ride || typeof ride !== 'object') continue;
    const rec = ride as Record<string, unknown>;
    const value =
      leg === 'pickup'
        ? firstNonEmptyString(
            rec.from_address,
            rec.fromAddress,
            rec.pickup_address,
            rec.pickupAddress,
            rec.origin_address,
            rec.originAddress
          )
        : firstNonEmptyString(
            rec.to_address,
            rec.toAddress,
            rec.dropoff_address,
            rec.dropoffAddress,
            rec.destination_address,
            rec.destinationAddress
          );
    if (value) return value;
  }
  return undefined;
}

export function resolveActivityStopAddress(activity: SimulationActivity | null | undefined): string | undefined {
  if (!activity) return undefined;
  const raw = activity as Record<string, unknown>;
  return firstNonEmptyString(
    activity.address,
    activity.location_name,
    activity.stop_name,
    raw.address,
    raw.address_formatted,
    raw.location_name,
    raw.locationName,
    raw.stop_name,
    raw.stopName,
    raw.label,
    raw.name
  );
}

export function resolveRidePointAddress(
  ridePoint: Partial<RideSchema> | Record<string, unknown> | null | undefined,
  leg: 'pickup' | 'dropoff'
): string | undefined {
  if (!ridePoint || typeof ridePoint !== 'object') return undefined;
  const rec = ridePoint as Record<string, unknown>;
  if (leg === 'pickup') {
    return firstNonEmptyString(
      rec.from_address,
      rec.fromAddress,
      rec.pickup_address,
      rec.pickupAddress,
      rec.origin_address,
      rec.originAddress
    );
  }
  return firstNonEmptyString(
    rec.to_address,
    rec.toAddress,
    rec.dropoff_address,
    rec.dropoffAddress,
    rec.destination_address,
    rec.destinationAddress
  );
}

export function resolveLegAddress(args: {
  leg: 'pickup' | 'dropoff';
  ridePoint?: Partial<RideSchema> | Record<string, unknown> | null;
  activity?: SimulationActivity | null;
  fallbackToStopAddress?: boolean;
  fallbackToCoords?: boolean;
}): string | undefined {
  const { leg, ridePoint, activity, fallbackToStopAddress = true, fallbackToCoords = true } = args;
  const fromRidePoint = resolveRidePointAddress(ridePoint, leg);
  if (fromRidePoint) return fromRidePoint;

  if (activity) {
    const raw = activity as Record<string, unknown>;
    const topLevelAddress =
      leg === 'pickup'
        ? firstNonEmptyString(
            raw.from_address,
            raw.fromAddress,
            raw.pickup_address,
            raw.pickupAddress,
            raw.origin_address,
            raw.originAddress
          )
        : firstNonEmptyString(
            raw.to_address,
            raw.toAddress,
            raw.dropoff_address,
            raw.dropoffAddress,
            raw.destination_address,
            raw.destinationAddress
          );
    if (topLevelAddress) return topLevelAddress;
  }

  const fromNestedTrip = extractNestedTripRideAddresses(activity, leg);
  if (fromNestedTrip) return fromNestedTrip;

  if (fallbackToStopAddress) {
    const stopAddress = resolveActivityStopAddress(activity);
    if (stopAddress) return stopAddress;
  }

  if (!fallbackToCoords || !activity) return undefined;
  const raw = activity as Record<string, unknown>;
  const lat = toFiniteNumber(activity.activity_latitude ?? activity.latitude ?? raw.lat);
  const lng = toFiniteNumber(activity.activity_longitude ?? activity.longitude ?? raw.lng ?? raw.lon);
  return formatCoords(lat, lng);
}

